import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import {
  APP_BASE_HREF,
  CommonModule,
  registerLocaleData,
} from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  FaIconLibrary,
  FontAwesomeModule,
} from "@fortawesome/angular-fontawesome";
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from "@ngx-translate/core";
import { ToastrModule } from "ngx-toastr";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

import localeSk from "@angular/common/locales/sk";
import localeCs from "@angular/common/locales/cs";
import localeEnUS from "@angular/common/locales/en";
import localeHu from "@angular/common/locales/hu";

registerLocaleData(localeEnUS, "en-US");
registerLocaleData(localeSk, "sk");
registerLocaleData(localeCs, "cs");
registerLocaleData(localeHu, "hu");
registerLocaleData(localeHu, "hu-HU");

import {
  faArrowLeft,
  faArrowRight,
  faArrowRotateLeft,
  faBarcode,
  faBars,
  faBarsStaggered,
  faBox,
  faCalculator,
  faCalendarCheck,
  faCamera,
  faCartShopping,
  faChartLine,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCirclePlus,
  faCircleXmark,
  faClock,
  faCommentDollar,
  faCreditCard,
  faEuroSign,
  faFileInvoice,
  faFloppyDisk,
  faGlobe,
  faHouse,
  faSearch,
  faMagnifyingGlass,
  faHandHoldingDollar,
  faMoneyBillWave,
  faMoneyCheckDollar,
  faPlane,
  faEarthEurope,
  faPlus,
  faPrint,
  faReceipt,
  faTicket,
  faTrash,
  faUmbrellaBeach,
  faArrowUpRightFromSquare,
  faBagShopping, 
  faSackDollar,
  faRug,
  faCashRegister,
  faBook,
  faMoneyCheck,
  faGift
} from "@fortawesome/free-solid-svg-icons";

import { SharedModule } from "./shared.module";
import { TranslationLoader } from "./services/translation-loader";
import { ApiConfig } from "./services/api.config.service";
import { PRMissingTranslationHandler } from "./services/missing-trans.handler";
import {
  DEFAULT_TIMEOUT,
  TimeoutInterceptor,
} from "./services/timeout-interceptor";
import { FormsModule } from "@angular/forms";
import { DesktopLayoutComponent } from "./desktop/layout/desktop-layout.component";
import { LanguageService } from "@dert-services";
import { SobitService } from "./services/sobit.service";
import { PopUpDialogInformalService } from "./services/confirms/popUpDialogInformal.service";
import { TranslateService } from "@ngx-translate/core";
import { PopUpInformalType } from "./components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { JwtInterceptor } from "./interceptors/jwt-interceptor";
import { ErrorHandlerInterceptor } from "./interceptors/error-handler.interceptor";

function setupTranslateFactory(languageService: LanguageService) {
  return async () => {
    const savedLang = languageService.getCurrentLanguage() || "en";
    await languageService.setLanguage(savedLang, false);
  };
}

export function setupSobitFactory(
  sobitService: SobitService,
  popUpDialogInformalService: PopUpDialogInformalService,
  translate: TranslateService,
): () => Promise<void> {
  return () => sobitService.loadSobitEcrScript().catch((error: unknown) => {
    console.error(error);

    window.setTimeout(() => {
      void popUpDialogInformalService.confirm({
        title: translate.instant("webPos.Errors.Error"),
        message: translate.instant("webPos.Errors.SobitScriptLoadError"),
        subMessage: translate.instant("webPos.OnlyOfflinePaymentAvailable"),
        type: PopUpInformalType.Error,
      });
    }, 0);

    return undefined;
  }).then(() => {});
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    FormsModule,
    DesktopLayoutComponent,
    FontAwesomeModule,
    SharedModule,
    AppRoutingModule,
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoader,
        deps: [HttpClient, ApiConfig],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: PRMissingTranslationHandler,
      },
    }),
    ToastrModule.forRoot({
      timeOut: 1500,
      positionClass: "toast-top-center",
      preventDuplicates: true,
    }),
    BrowserAnimationsModule,
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: "/" },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: "auto" },
    },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: 60000 },
    { provide: LOCALE_ID, useValue: "cs" },
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateFactory,
      deps: [LanguageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: setupSobitFactory,
      deps: [SobitService, PopUpDialogInformalService, TranslateService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(
      faArrowLeft,
      faArrowRight,
      faArrowRotateLeft,
      faBarcode,
      faBars,
      faBarsStaggered,
      faBox,
      faCalculator,
      faCalendarCheck,
      faCamera,
      faCartShopping,
      faChartLine,
      faCheck,
      faChevronDown,
      faChevronLeft,
      faCircle,
      faCircleCheck,
      faCircleExclamation,
      faCircleInfo,
      faCartShopping,
      faArrowRotateLeft,
      faClock,
      faCalendarCheck,
      faBarsStaggered,
      faPlus,
      faPrint,
      faReceipt,
      faMoneyBillWave,
      faHandHoldingDollar,
      faUmbrellaBeach,
      faPlane,
      faEarthEurope,
      faTicket,
      faMoneyCheck,
      faMoneyCheckDollar,
      faFloppyDisk,
      faCirclePlus,
      faCircleXmark,
      faFileInvoice,
      faGlobe,
      faHouse,
      faSearch,
      faMagnifyingGlass,
      faTrash,
      faArrowUpRightFromSquare,
      faBagShopping, 
      faSackDollar,
      faRug,
      faCreditCard,
      faCashRegister,
      faBook,
      faGift,
    );
  }
}
